import React, {Suspense, useEffect, useRef, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import {TabType} from '@di-shared/features/home/kr/HomeContents.kr';
import EvBatteryMonitor from '@di-shared/features/home/kr/ev-battery/monitor/EVBatteryMonitor';
import ParticipatoryEvBatteryCare from '@di-shared/features/home/kr/ev-battery/participatory-care/ParticipatoryEvBatteryCare';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import RenderIfBatteryDataKr from '@di-shared/features/home/kr/vehicle/RenderIfBatteryData.kr';
import RenderIfBrakePadDataKr from '@di-shared/features/home/kr/vehicle/RenderIfBrakePadData.kr';
import RenderIfTireDataKr from '@di-shared/features/home/kr/vehicle/RenderIfTireData.kr';
import VehicleBatteryKr from '@di-shared/features/home/kr/vehicle/VehicleBattery.kr';
import VehicleBrakePadKr from '@di-shared/features/home/kr/vehicle/VehicleBrakePad.kr';
import VehicleCertificationGradeKr from '@di-shared/features/home/kr/vehicle/VehicleCertificationGrade.kr';
import VehicleDiagLoadingKr from '@di-shared/features/home/kr/vehicle/VehicleDiagLoading.kr';
import VehicleSestKr from '@di-shared/features/home/kr/vehicle/VehicleSest.kr';
import VehicleTireKr from '@di-shared/features/home/kr/vehicle/VehicleTire.kr';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {WebviewUtils} from '@shared/webviewUtils';

export interface VehicleContentsKrProps {
  vin: string;
  puid: string;
  ccid: string;
}

enum TileIndex {
  TIRE,
  BATTERY,
  BRAKE,
  CPO,
}

const KR_SEST_AUTO_SYNC_SUSPENSION_POPUP_HIDDEN_UNTIL_KEY = 'kr-sest-auto-sync-suspension-popup-hidden-until';
const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

function VehicleContentsKr({vin, puid, ccid}: VehicleContentsKrProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {resolveFeatureFlag, getExternalVariable} = useFirebaseStore(({resolveFeatureFlag, getExternalVariable}) => ({
    resolveFeatureFlag,
    getExternalVariable,
  }));
  const entryAlertOpenedRef = useRef(false);
  const {checkAvailableComponent, isAgreeFeature} = useAvailableComponentKr({vin}); // suspense
  const [tileVisibility, setTileVisibility] = useState<boolean[]>([
    !!checkAvailableComponent(PermissionTypeKr.TIRE),
    !!checkAvailableComponent(PermissionTypeKr.BATTERY_LOW),
    !!checkAvailableComponent(PermissionTypeKr.BRAKE),
    !!checkAvailableComponent(PermissionTypeKr.CPO),
  ]);
  const numberOfTilesBeforeVehicleCertificationGrade = tileVisibility.slice(0, 3).filter(Boolean).length;
  const isVehicleCertificationGradeExtendeded =
    numberOfTilesBeforeVehicleCertificationGrade === 0 || numberOfTilesBeforeVehicleCertificationGrade === 2;
  const isSestAutoSyncSuspensionPopupVisible = resolveFeatureFlag('KR_SEST_AUTO_SYNC_SUSPENSION_POPUP', vin);
  const parsedSestAutoSyncSuspensionStartDate = parseDateWithFormat(getExternalVariable('KR_SEST_AUTO_SYNC_SUSPENSION_START_DATE'));
  const sestAutoSyncSuspensionStartDate = parsedSestAutoSyncSuspensionStartDate
    ? CldrDatetimeFormatter.format(parsedSestAutoSyncSuspensionStartDate, 'yMMMEd')
    : CldrDatetimeFormatter.format(new Date('2026-06-24'), 'yMMMEd');
  const sestAutoSyncSuspensionTel = getExternalVariable('KR_SEST_AUTO_SYNC_SUSPENSION_TEL');

  function changeTileInvisible(index: TileIndex) {
    setTileVisibility((prevTiles) => {
      const updatedTiles = [...prevTiles];
      updatedTiles[index] = false;
      return updatedTiles;
    });
  }

  useEffect(() => {
    if (!isSestAutoSyncSuspensionPopupVisible || entryAlertOpenedRef.current) {
      return;
    }

    entryAlertOpenedRef.current = true;

    const hiddenUntil = Number(window.localStorage.getItem(KR_SEST_AUTO_SYNC_SUSPENSION_POPUP_HIDDEN_UNTIL_KEY));

    if (Number.isFinite(hiddenUntil) && hiddenUntil > Date.now()) {
      return;
    }

    if (Number.isFinite(hiddenUntil) && hiddenUntil <= Date.now()) {
      window.localStorage.removeItem(KR_SEST_AUTO_SYNC_SUSPENSION_POPUP_HIDDEN_UNTIL_KEY);
    }

    async function openVehicleEntryAlert() {
      const selectedIndex = await WebviewUtils.openCustomAlert({
        title: t('T40005'),
        message: t('T30640_$date_$tel', {date: sestAutoSyncSuspensionStartDate, tel: sestAutoSyncSuspensionTel}),
        buttons: [
          {text: t('T10114'), style: 'PRIMARY', actionType: 'DEFAULT'},
          {text: t('T20178'), style: 'BASIC', actionType: 'CANCEL'},
        ],
      });

      if (selectedIndex === 1) {
        window.localStorage.setItem(KR_SEST_AUTO_SYNC_SUSPENSION_POPUP_HIDDEN_UNTIL_KEY, String(Date.now() + ONE_WEEK_IN_MS));
      }
    }

    openVehicleEntryAlert();
  }, [isSestAutoSyncSuspensionPopupVisible, sestAutoSyncSuspensionStartDate, sestAutoSyncSuspensionTel, t]);

  /**
   * [2024-11-28:Jeyoung Lee] FIXME:
   * error 컴포넌트에서 LinkTo -> customalert로 바꿔야함
   * 테스트도 한번씩 해봐야됨
   *
   * fixme:
   * 브레이크패드의 경우 error component랑 update component가 달랐음 -> 아직 협의 중
   * @link https://hmg.dooray.com/project/tasks/3946597057095753039
   */

  return (
    <div className="tab_content car_management">
      <div className="car_management_header"></div>
      <section
        className="section"
        id={`${TabType.Vehicle}_tabpanel`}
        aria-labelledby={`${TabType.Vehicle}_tab`}
      >
        <h2 className="blind">{t('T20050')}</h2>
        {/* 차량진단 */}
        <section className="section section_car_diagnosis">
          <h3 className="blind">{t('T10103')}</h3>
          <ul className="car_diagnosis_list">
            {/* 타이어 공기압 */}
            {checkAvailableComponent(PermissionTypeKr.TIRE) && (
              <ErrorBoundary fallback={<VehicleTireKr.Error />}>
                <Suspense fallback={<VehicleDiagLoadingKr />}>
                  <RenderIfTireDataKr
                    vin={vin}
                    onNoData={() => changeTileInvisible(TileIndex.TIRE)}
                  >
                    <VehicleTireKr vin={vin} />
                  </RenderIfTireDataKr>
                </Suspense>
              </ErrorBoundary>
            )}
            {/* 12V 배터리 */}
            {checkAvailableComponent(PermissionTypeKr.BATTERY_LOW) && (
              <ErrorBoundary fallback={<VehicleBatteryKr.Error />}>
                <Suspense fallback={<VehicleDiagLoadingKr />}>
                  <RenderIfBatteryDataKr
                    vin={vin}
                    onNoData={() => changeTileInvisible(TileIndex.BATTERY)}
                  >
                    <VehicleBatteryKr vin={vin} />
                  </RenderIfBatteryDataKr>
                </Suspense>
              </ErrorBoundary>
            )}
            {/* 브레이크 패드 */}
            {checkAvailableComponent(PermissionTypeKr.BRAKE) && (
              <ErrorBoundary fallback={<VehicleBrakePadKr.Error />}>
                <Suspense fallback={<VehicleDiagLoadingKr />}>
                  <RenderIfBrakePadDataKr
                    vin={vin}
                    onNoData={() => changeTileInvisible(TileIndex.BRAKE)}
                  >
                    <VehicleBrakePadKr vin={vin} />
                  </RenderIfBrakePadDataKr>
                </Suspense>
              </ErrorBoundary>
            )}

            {/* 차량 인증 등급 (현대/제네시스) */}
            {checkAvailableComponent(PermissionTypeKr.CPO) && (
              <VehicleCertificationGradeKr
                vin={vin}
                isExtended={isVehicleCertificationGradeExtendeded}
              />
            )}
          </ul>
        </section>
        {/* EV배터리 공통 */}
        {checkAvailableComponent(PermissionTypeKr.BATTERY_PFRM_MNG_DI_SERVICE) &&
          isAgreeFeature(PermissionTypeKr.BATTERY_PFRM_MNG_DI_SERVICE) && (
            <EvBatteryMonitor
              vin={vin}
              puid={puid}
            />
          )}
        {/*참여형 EV 배터리 케어 현대캐피탈 특화 정보 */}
        {checkAvailableComponent(PermissionTypeKr.BATTERY_PFRM_MNG_SERVICE) &&
          isAgreeFeature(PermissionTypeKr.BATTERY_PFRM_MNG_SERVICE) && (
            <ParticipatoryEvBatteryCare
              vin={vin}
              puid={puid}
            />
          )}

        {/* 소모품 */}
        {checkAvailableComponent(PermissionTypeKr.CONSUMABLES) && (
          <VehicleSestKr
            vin={vin}
            ccid={ccid}
          />
        )}
      </section>
    </div>
  );
}

export default VehicleContentsKr;
